import * as API from './api'

declare var Accept: any

// The Authorize.Net Accept.js tokenization flow, shared by the control and
// redesign checkout forms. Framework-agnostic: the React glue that owns the
// `acceptJsId`/`paymentToken` state lives in `AbstractMembershipCheckoutForm`.

const ACCEPT_JS_PRODUCTION_URI = 'https://js.authorize.net/v1/Accept.js'
const ACCEPT_JS_SANDBOX_URI = 'https://jstest.authorize.net/v1/Accept.js'

// Re-exported so existing `from './lib/accept_js'` import sites keep working.
export type ITokenizableCard = API.ITokenizableCard

export interface IParsedTokenResponse {
  paymentToken: API.IPaymentToken
  paymentTokenErrors: API.IAuthErrors[]
}

// Inject the Accept.js <script> for the given environment, returning the id of
// the tag it created so the caller can remove that exact tag later. `onLoad`
// fires with the same id once the script finishes loading. Callers guard on
// the presence of `authNet` before invoking.
export function loadAcceptJsScript(
  authNet: API.IAuthorizeNet,
  onLoad: (acceptJsId: string) => void,
): string {
  const scriptUri = authNet.test_mode ? ACCEPT_JS_SANDBOX_URI : ACCEPT_JS_PRODUCTION_URI
  const scriptTag = document.createElement('script')
  scriptTag.id = 'acceptJs' + Math.random().toString(20).slice(2)
  scriptTag.src = scriptUri
  scriptTag.addEventListener('load', () => onLoad(scriptTag.id))
  document.body.appendChild(scriptTag)
  return scriptTag.id
}

// Remove a script tag previously added by `loadAcceptJsScript`.
export function removeAcceptJsScript(acceptJsId: string): void {
  const scriptTag = document.querySelector('#' + acceptJsId)
  if (scriptTag && scriptTag.parentNode) {
    scriptTag.parentNode.removeChild(scriptTag)
  }
}

// Send the card to Accept.js for tokenization. The response is delivered
// asynchronously to `onResponse`; translate it with `parseTokenResponse`.
export function tokenizeCard(
  authNet: API.IAuthorizeNet,
  card: ITokenizableCard,
  onResponse: (response: any) => void,
): void {
  const data = {
    authData: {
      clientKey: authNet.public_client_key,
      apiLoginID: authNet.api_login_id,
    },
    cardData: {
      cardNumber: card.card_number,
      month: card.exp_month,
      year: card.exp_year,
      cardCode: card.cvv,
    },
  }

  Accept.dispatchData(data, onResponse)
}

// Translate an Accept.js `dispatchData` response into the checkout's token +
// error shape. An `Error` result yields a blank token plus one entry per
// message; a success yields the opaque data. The submitted card is echoed back
// onto the token in both cases so the form can re-render it. Pure — any side
// effect (scroll-to-top on error) belongs to the caller.
export function parseTokenResponse(card: ITokenizableCard, tokenResponse: any): IParsedTokenResponse {
  if (tokenResponse.messages.resultCode === 'Error') {
    const paymentTokenErrors: API.IAuthErrors[] = tokenResponse.messages.message.map(
      (message: any) => ({ code: message.code, text: message.text }),
    )

    return {
      paymentToken: { dataDescriptor: '', dataValue: '', creditCard: card },
      paymentTokenErrors,
    }
  }

  return {
    paymentToken: {
      dataDescriptor: tokenResponse.opaqueData.dataDescriptor,
      dataValue: tokenResponse.opaqueData.dataValue,
      creditCard: card,
    },
    paymentTokenErrors: [],
  }
}
